package dairy.mynotes.updatenotes

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import dairy.mynotes.navigation.Navigator
import dairy.mynotes.reusablecomponents.AppTopBar
import dairy.mynotes.reusablecomponents.BackIcon
import dairy.mynotes.reusablecomponents.ButtonComponent
import dairy.mynotes.reusablecomponents.CircularProgressBarComponent
import dairy.mynotes.reusablecomponents.TextInputComponent
import dairy.mynotes.ui.theme.AppColors

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UpdateNotesScreen(
    noteId: String,
    isEditMode: Boolean = false,
    navigator: Navigator? = null,
    updateNotesViewModel: UpdateNotesViewModel = viewModel { UpdateNotesViewModel() }
) {
    val uiState by updateNotesViewModel.uiState.collectAsState()

    // Load note when screen opens
    LaunchedEffect(noteId) {
        updateNotesViewModel.loadNote(noteId)
    }

    // Navigate back after save or delete
    LaunchedEffect(uiState.isSaved) {
        if (uiState.isSaved) navigator?.navigateBack()
    }
    LaunchedEffect(uiState.isDeleted) {
        if (uiState.isDeleted) navigator?.navigateBack()
    }

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            AppTopBar(
                title = uiState.category.ifBlank { "Note" },
                navigator = navigator,
                showBack = true
            )
        }
    ) { paddingValues ->
        if (uiState.isLoading && uiState.content.isEmpty()) {
            Box(
                modifier = Modifier.fillMaxSize().padding(paddingValues),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = AppColors.Primary)
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .padding(horizontal = 20.dp, vertical = 20.dp)
                    .verticalScroll(rememberScrollState())
            ) {
                // Category chip (read-only always)
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.padding(bottom = 20.dp)
                ) {
                    Text(
                    text = "Category",
                    fontSize = 15.sp,
                        fontWeight = FontWeight.Medium,
                        color = AppColors.TextSecondary
                    )
                    Spacer(modifier = Modifier.width(12.dp))
                    Box(
                        modifier = Modifier
                            .background(AppColors.CategoryBadgeBg, RoundedCornerShape(20.dp))
                            .padding(horizontal = 14.dp, vertical = 6.dp)
                    ) {
                        Text(
                            text = uiState.category.ifBlank { "—" },
                            fontSize = 16.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = AppColors.CategoryBadgeText
                        )
                    }
                }

                // Description
                if (isEditMode) {
                    TextInputComponent(
                        label = "Description",
                        value = uiState.content,
                        onValueChange = updateNotesViewModel::onContentChange,
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(160.dp)
                    )
                } else {
                    // View-only mode
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(AppColors.SurfaceVariant, RoundedCornerShape(12.dp))
                            .padding(16.dp)
                    ) {
                        Text(
                            text = "Description",
                            fontSize = 13.sp,
                            color = AppColors.TextSecondary,
                            modifier = Modifier.padding(bottom = 8.dp)
                        )
                        Text(
                            text = uiState.content,
                            fontSize = 16.sp,
                            color = AppColors.TextPrimary,
                            lineHeight = 24.sp
                        )
                    }
                }

                // Error
                uiState.error?.let {
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = it,
                        color = AppColors.Error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                if (uiState.isLoading) {
                    Spacer(modifier = Modifier.height(16.dp))
                    CircularProgressBarComponent(modifier = Modifier.align(Alignment.CenterHorizontally))
                }

                // Buttons – only in edit mode
                if (isEditMode) {
                    Spacer(modifier = Modifier.height(24.dp))
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        ButtonComponent(
                            text = "Cancel",
                            modifier = Modifier.weight(1f).height(52.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AppColors.SurfaceVariant,
                                contentColor = AppColors.TextPrimary
                            ),
                            textColor = AppColors.TextPrimary,
                            onClick = { navigator?.navigateBack() },
                            enabled = !uiState.isLoading
                        )
                        ButtonComponent(
                            text = "Save",
                            modifier = Modifier.weight(1f).height(52.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AppColors.Primary,
                                contentColor = AppColors.OnPrimary
                            ),
                            onClick = updateNotesViewModel::saveNote,
                            enabled = !uiState.isLoading
                        )
                    }
                    // Extra space so buttons clear the keyboard
                    Spacer(modifier = Modifier.height(24.dp))
                }
            }
        }
    }
}
